/**
 * Keeping installed apps current (mobile M5, M8; NitHub app contract §5).
 *
 * Checked once at launch, in the installed apps only — a browser always loads
 * the server's own current bundle.
 *
 *  * **iPhone home-screen app**: if the server needs a newer bundle
 *    (`min_client`), "Update" reloads, and the service worker fetches it.
 *  * **Android app**: `/api/version`'s `android` object names the current APK.
 *    A newer one is an **offer** — a bar above the tab bar, "Later" hides it
 *    for that version. Below `min_version_code` (or `min_client`) it is a
 *    **wall**, still with every way to update, so nobody is locked out.
 *    NitHub on the phone → "Update in NitHub" (it can then update silently);
 *    otherwise the app downloads the APK itself, checks its SHA-256 and opens
 *    Android's installer, with "Get NitHub" beside it.
 */

import { Download, RefreshCw } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/primitives";
import { clientType, isAndroidApp } from "@/platform";
import { getNitHub, nitHubStatus, openInNitHub, type NitHubStatus } from "@/platform/nithub";
import {
  decideUpdate,
  downloadAndInstall,
  fetchVersion,
  installedVersionCode,
  openInstallSettings,
  tooOld,
  type AndroidRelease,
  type UpdateDecision,
} from "@/platform/update";

const LATER_KEY = "mydeck.update.later";

function laterFor(): string | null {
  try {
    return localStorage.getItem(LATER_KEY);
  } catch {
    return null;
  }
}

export function UpdateNeeded() {
  const [decision, setDecision] = React.useState<UpdateDecision>({ kind: "none" });
  const [nithub, setNitHub] = React.useState<NitHubStatus | null>(null);

  React.useEffect(() => {
    if (clientType() === "web") return;
    void (async () => {
      const info = await fetchVersion();
      if (!isAndroidApp()) {
        if (tooOld(info)) setDecision({ kind: "wall", release: null });
        return;
      }
      const code = (await installedVersionCode()) ?? 0;
      const d = decideUpdate(info, code);
      if (d.kind === "offer" && laterFor() === d.release.version_name) return;
      setDecision(d);
      if (d.kind !== "none") setNitHub(await nitHubStatus());
    })();
  }, []);

  if (decision.kind === "none") return null;

  const later = () => {
    if (decision.kind === "offer") {
      try {
        localStorage.setItem(LATER_KEY, decision.release.version_name);
      } catch {
        /* the bar comes back next launch */
      }
    }
    setDecision({ kind: "none" });
  };

  const wall = decision.kind === "wall";
  const release = decision.release;
  return (
    <div
      role={wall ? "alertdialog" : "status"}
      aria-labelledby="update-title"
      className={
        wall
          ? "fixed inset-0 z-[95] flex flex-col justify-end bg-base/95 px-5 pb-[calc(1.5rem+var(--inset-bottom))] backdrop-blur-xl"
          : "fixed inset-x-3 bottom-[calc(4.75rem+var(--inset-bottom))] z-[45] rounded-2xl border border-brass/30 bg-panel/95 p-4 shadow-lift backdrop-blur-xl md:bottom-4 md:left-auto md:w-[24rem]"
      }
    >
      <p id="update-title" className="font-display text-base font-semibold text-ink">
        {wall
          ? "This version of MyDeck is out of date"
          : `MyDeck ${release?.version_name ?? ""} is ready`}
      </p>
      <p className="mt-1 text-xs text-muted">
        {wall
          ? "The server has moved on, so this version can't be used any more. Updating keeps your sign-in."
          : "A newer version is available."}
      </p>
      {release?.notes && release.notes.length > 0 && (
        <ul className="mt-2 list-disc space-y-0.5 pl-4 text-xs text-ink">
          {release.notes.slice(0, 5).map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      )}
      <UpdateActions release={release} nithub={nithub} />
      {!wall && (
        <Button variant="ghost" size="sm" className="mt-1 w-full" onClick={later}>
          Later
        </Button>
      )}
    </div>
  );
}

function UpdateActions({ release, nithub }: { release: AndroidRelease | null; nithub: NitHubStatus | null }) {
  const [state, setState] = React.useState<{ busy: boolean; progress: number | null; message: string | null; needsPermission: boolean }>({
    busy: false,
    progress: null,
    message: null,
    needsPermission: false,
  });

  // The home-screen app: the service worker fetches the new shell on reload.
  if (!isAndroidApp() || !release) {
    return (
      <Button variant="primary" className="mt-3 w-full" onClick={() => window.location.reload()}>
        <RefreshCw className="h-4 w-4" aria-hidden />
        Update MyDeck
      </Button>
    );
  }

  if (nithub?.installed) {
    return (
      <Button variant="primary" className="mt-3 w-full" onClick={() => void openInNitHub()}>
        Update in NitHub
      </Button>
    );
  }

  const install = async () => {
    setState({ busy: true, progress: 0, message: null, needsPermission: false });
    const outcome = await downloadAndInstall(release, (p) => setState((s) => ({ ...s, progress: p })));
    const words: Record<string, string | null> = {
      installing: null,
      "not-permitted": "Android needs your OK first: allow MyDeck to install apps, then tap Download again.",
      "checksum-mismatch": "The download didn't match the published checksum, so it wasn't installed. Try again.",
      "download-failed": "The download didn't finish. Check the connection and try again.",
      unsupported: "This build can't update itself. Get the new version from cards.nithishb.com/download.",
    };
    setState({ busy: false, progress: null, message: words[outcome] ?? null, needsPermission: outcome === "not-permitted" });
  };

  return (
    <div className="mt-3 space-y-2">
      <Button variant="primary" className="w-full" disabled={state.busy} onClick={() => void install()}>
        <Download className="h-4 w-4" aria-hidden />
        {state.busy
          ? state.progress !== null && state.progress > 0
            ? `Downloading… ${Math.round(state.progress * 100)}%`
            : "Downloading…"
          : `Download and install (${(release.size / 1_048_576).toFixed(1)} MB)`}
      </Button>
      {state.message && <p className="text-xs text-warn">{state.message}</p>}
      {state.needsPermission && (
        <Button variant="default" className="w-full" onClick={() => void openInstallSettings()}>
          Allow installs
        </Button>
      )}
      <Button variant="ghost" size="sm" className="w-full" onClick={getNitHub}>
        Get NitHub — it keeps all our apps up to date
      </Button>
    </div>
  );
}
